/**
 * EPIC 47 — in-app notification bell, shared by the admin + worker shells.
 * Reads the signed-in user's own /notifications (read-own rule), shows an unread
 * dot/count, and lists the most recent items in a dropdown. Clicking an item
 * navigates to its `link` (the thread), where markConversationRead settles it.
 *
 * P2a adds an EXPLICIT push opt-in row ("Luba teavitused") at the bottom of the
 * menu (permission is NEVER requested on load) plus a foreground-message toast so
 * a push that lands while the app is open isn't silently dropped.
 */

import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Menu,
  Snackbar,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import {
  currentPushState,
  enablePush,
  onForegroundPush,
  setOptionalPushEnabled,
  subscribeOptionalPushEnabled,
  type PushState,
} from '../services/push';
import { markNotificationsRead } from '../services/messaging';
import { useMyNotifications } from '../services/useMessaging';

import { BADGE_MAX, unreadLabel } from './unreadLabel';

function fmtTime(v: unknown): string {
  let ms = 0;
  if (v && typeof v === 'object' && 'toMillis' in v && typeof (v as { toMillis: () => number }).toMillis === 'function') {
    ms = (v as { toMillis: () => number }).toMillis();
  } else if (typeof v === 'string') {
    ms = Date.parse(v) || 0;
  }
  if (!ms) return '';
  return new Date(ms).toLocaleString('et-EE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function NotificationBell() {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const navigate = useNavigate();
  const { notifications, unreadCount } = useMyNotifications(user?.uid);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [pushState, setPushState] = useState<PushState>('default');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [optionalOn, setOptionalOn] = useState(true);

  useEffect(() => {
    if (!user?.uid) return;
    return subscribeOptionalPushEnabled(user.uid, setOptionalOn);
  }, [user?.uid]);

  const toggleOptional = async (next: boolean) => {
    setOptionalOn(next);
    try {
      await setOptionalPushEnabled(next);
    } catch {
      setOptionalOn(!next);
    }
  };

  // Read the (permission-free) push state on mount + a foreground toast listener.
  useEffect(() => {
    setPushState(currentPushState());
    const unsub = onForegroundPush((payload) => {
      const title = payload.notification?.title ?? '';
      const body = payload.notification?.body ?? '';
      setToast([title, body].filter(Boolean).join(' — '));
    });
    return unsub;
  }, []);

  const recent = notifications.slice(0, 12);

  const open = (n: (typeof notifications)[number]) => {
    setAnchor(null);
    // Settle it here rather than at the destination: only chat threads have
    // somewhere that knows to clear them, and an invoice notice has no such
    // page. Not awaited — the badge catching up a moment later is better than
    // holding the navigation.
    if (!n.read) void markNotificationsRead({ notificationIds: [n.id] });
    if (n.link) navigate(n.link);
  };

  const markAllRead = () => {
    setAnchor(null);
    void markNotificationsRead({ all: true });
  };

  const pushLabel = (): string => {
    switch (pushState) {
      case 'enabled':
        return t('push.enabled');
      case 'denied':
        return t('push.blocked');
      case 'not-configured':
        return t('push.notConfigured');
      case 'unsupported':
        return t('push.unsupported');
      default:
        return busy ? t('push.enabling') : t('push.enable');
    }
  };

  const onEnable = async () => {
    if (busy || pushState === 'enabled') return;
    setBusy(true);
    try {
      const next = await enablePush();
      setPushState(next);
      if (next === 'enabled') setToast(t('push.enabled'));
    } catch {
      setPushState(currentPushState());
    } finally {
      setBusy(false);
    }
  };

  // The row is only actionable when we can still ask for / obtain permission.
  const pushActionable = pushState === 'default' && !busy;

  return (
    <>
      <Tooltip title={t('notifications.title') ?? ''}>
        <IconButton
          aria-label={unreadLabel(t, t('notifications.title'), unreadCount)}
          onClick={(e) => setAnchor(e.currentTarget)}
        >
          {unreadCount > 0 ? (
            <Badge color="error" badgeContent={unreadCount} max={BADGE_MAX}>
              <NotificationsNoneOutlinedIcon />
            </Badge>
          ) : (
            <NotificationsNoneOutlinedIcon />
          )}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 340, maxWidth: '90vw' } } }}
      >
        <Box
          sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flexGrow: 1 }}>
            {t('notifications.title')}
          </Typography>
          {unreadCount > 0 && (
            <Typography
              component="button"
              variant="caption"
              onClick={markAllRead}
              sx={{
                border: 0,
                background: 'none',
                p: 0,
                cursor: 'pointer',
                color: 'primary.main',
                font: 'inherit',
                fontSize: '0.75rem',
              }}
            >
              {t('notifications.markAllRead', { defaultValue: 'Märgi kõik loetuks' })}
            </Typography>
          )}
        </Box>
        <Divider />
        {recent.length === 0 ? (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('notifications.empty')}
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ maxHeight: 360, overflowY: 'auto' }}>
            {recent.map((n) => (
              <ListItemButton
                key={n.id}
                onClick={() => open(n)}
                sx={{ alignItems: 'flex-start', bgcolor: n.read ? 'transparent' : 'action.hover' }}
              >
                <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                  {/* Prefer the i18n key: the reader may have switched
                      language since the notification was written, and stored
                      prose cannot follow them. Chat notifications and anything
                      written before keys existed still have title/body. */}
                  <Typography variant="body2" sx={{ fontWeight: n.read ? 500 : 700 }} noWrap>
                    {n.titleKey ? t(n.titleKey, { ...n.params, defaultValue: n.title }) : n.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                    {n.bodyKey ? t(n.bodyKey, { ...n.params, defaultValue: n.body }) : n.body}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {fmtTime(n.createdAt)}
                  </Typography>
                </Stack>
              </ListItemButton>
            ))}
          </List>
        )}
        <Divider />
        <ListItemButton
          onClick={() => void onEnable()}
          disabled={!pushActionable}
          sx={{ gap: 1 }}
        >
          <NotificationsActiveOutlinedIcon
            fontSize="small"
            color={pushState === 'enabled' ? 'success' : 'action'}
          />
          <Typography
            variant="body2"
            color={pushState === 'denied' || pushState === 'not-configured' ? 'text.secondary' : 'text.primary'}
          >
            {pushLabel()}
          </Typography>
        </ListItemButton>
        {/* One switch for optional pushes; mandatory ones always come. */}
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2">{t('push.optional')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('push.optionalHint')}
            </Typography>
          </Box>
          <Switch
            size="small"
            checked={optionalOn}
            onChange={(e) => void toggleOptional(e.target.checked)}
            slotProps={{ input: { 'aria-label': t('push.optional') } }}
          />
        </Box>
      </Menu>
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </>
  );
}
